import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import ApiRequestProjectsByUsersGraph from '../../api/ApiRequestProjectsByUserGraph';
import { getLast5Months } from '../../data/months';

const options = {
    chart: {
        animations: {
            enabled: false, // Desactivar las animaciones
        },
        height: 350,
        maxWidth: "100%",
        type: "area",
        fontFamily: "Inter, sans-serif",
        dropShadow: {
            enabled: false,
        },
        toolbar: {
            show: false,
        },
    },
    tooltip: {
        z: {
            formatter: function (seriesName, opts) {
                if (opts.w.globals.series[opts.seriesIndex][opts.dataPointIndex] === 0) {
                    return undefined;
                }
                return seriesName;
            }
        },
        
    },
    fill: {
        type: "gradient",
        gradient: {
            opacityFrom: 0.55,
            opacityTo: 0,
            shade: "#1C64F2",
            gradientToColors: ["#1C64F2"],
        },
    },
    dataLabels: {
        enabled: false,
    },
    stroke: {
        curve: "smooth",
        width: 2,
    },
    grid: {
        show: false,
        strokeDashArray: 4,
    },
    markers: {
        size: 0,
    },
    series: [],
    xaxis: {
        categories: [],
        labels: {
            show: true,
            style: {
                colors: '#9CA3AF',
            }
        },
        axisBorder: {
            show: false,
        },
        axisTicks: {
            show: false,
        },
    },
    yaxis: {
        show: true,
        labels: {
            show: true,
            style: {
                colors: '#9CA3AF',
            }
        },
    },
};

export const ProjectsByUser = () => {
    const chartRef = useRef(null);
    const { apiData, isLoading } = ApiRequestProjectsByUsersGraph();
    const [search, setSearch] = useState('');
    const last5Months = getLast5Months();
    
    // Paginación
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    const graphData = useMemo(() => apiData && Array.isArray(apiData) ? apiData : [], [apiData]);
    const filteredGraphData = graphData
        .filter(user => user.name.toLowerCase().includes(search.toLowerCase()))
        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage) // Paginación
        .sort((a, b) => { // Sorting
            // Bucle que recorre los últimos meses de datos de cada usuario
            for (let i = a.data.length - 1; i >= 0; i--) {
                // Obtiene el valor de datos del usuario a en el mes actual
                const aValue = a.data[i] || 0; // Si el valor es falso, se asigna 0
                // Obtiene el valor de datos del usuario b en el mes actual
                const bValue = b.data[i] || 0; // Si el valor es falso, se asigna 0
                
                // Compara los valores de datos del usuario a y b en el mes actual
                if (aValue !== bValue) {
                    // Si los valores son diferentes, devuelve la diferencia para ordenar
                    return bValue - aValue; // Ordena de mayor a menor
                }
            }
            // Si todas las posiciones de datos son iguales, ordena por el nombre del usuario
            return a.name.localeCompare(b.name); // Ordena alfabéticamente
        });
    const series = filteredGraphData.map(user => ({ name: user.name, data: user.data }));

    useLayoutEffect(() => {
        if (!isLoading && apiData) {
            if (chartRef.current) {
                chartRef.current.updateSeries(series);
                chartRef.current.updateOptions({ xaxis: { categories: last5Months } });
            } else {
                options.series = series;
                options.xaxis.categories = last5Months;
                chartRef.current = new ApexCharts(document.getElementById("chartByUser"), options);
                chartRef.current.render();
            }
        }
    }, [apiData, isLoading, series, last5Months]);

    return (
        
        <div>
            {isLoading ? (
                <div className="flex justify-center items-center">
                    <div role="status">
                        <svg aria-hidden="true" className="inline w-10 h-10 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                        </svg>
                    </div>
                </div>
            ) : (
                <>
                    <div className="mt-4">
                        <div>
                            <div id="chartByUser"></div>
                            <div className="mb-1 flex justify-center">
                                <span className="text-gray-500 dark:text-gray-400">
                                     {currentPage} / {Math.ceil(graphData.length / itemsPerPage)}
                                </span>
                            </div>
                            <div className="mb-4 flex justify-center">
                                <button
                                    type="button"
                                    className={`rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:w-auto dark:bg-gray-800 dark:text-gray-400 ${currentPage === 1 ? 'opacity-50' : 'hover:bg-gray-50 hover:ring-gray-400 dark:hover:bg-gray-600'}`}
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                >
                                    {"<"}
                                </button>
                                <div className="w-2"></div>
                                <button
                                    type="button"
                                    className={`rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:w-auto dark:bg-gray-800 dark:text-gray-400 ${filteredGraphData.length < itemsPerPage ? 'opacity-50' : 'hover:bg-gray-50 hover:ring-gray-400 dark:hover:bg-gray-600'}`}
                                    disabled={filteredGraphData.length < itemsPerPage}
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                >
                                    {">"}
                                </button>
                            </div>
                            <div className="relative w-full">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg aria-hidden="true" className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <input
                                    type="search"
                                    id="simple-search"
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                                    value={search}
                                    onChange={e => {
                                        setSearch(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    placeholder="Buscar usuario..."
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
